import { useEffect, useRef, useState } from 'react';
import { FileText, Image, FileSpreadsheet, Download, Upload, File, ChevronDown, ChevronRight, Eye, X } from 'lucide-react';
import API from '../../lib/api';

const DOC_LABELS = {
  pan_card:               'PAN Card',
  aadhar_card:            'Aadhaar Card',
  form_16:                'Form 16',
  bank_statement:         'Bank Statement',
  salary_slip:            'Salary Slip',
  investment_proof:       'Investment Proof',
  home_loan_certificate:  'Home Loan Certificate',
  rent_receipt:           'Rent Receipt',
  capital_gains_statement:'Capital Gains Statement',
  other:                  'Other Document',
  company_incorporation_docs: 'Company Incorporation Docs',
  llp_incorporation_docs: 'LLP Incorporation Docs',
  balance_sheet:          'Balance Sheet',
  profit_loss:            'Profit & Loss Statement',
  gst_return:             'GST Return',
  tds_certificate:        'TDS Certificate',
  itr_copy:               'Previous ITR Copy',
  payment_proof:          'Payment Proof',
};

function docLabel(doc_type) {
  if (!doc_type) return 'Document';
  if (DOC_LABELS[doc_type]) return DOC_LABELS[doc_type];
  // No longer suffix " (Admin)" — the actual uploader name + role pill on
  // the row carries that information and reflects the real uploader (CA,
  // Super Admin, etc.) instead of a blanket "Admin" tag.
  if (doc_type.startsWith('admin_')) return doc_type.replace('admin_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  if (doc_type.startsWith('cycle_')) return doc_type.replace('cycle_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return doc_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function mimeIcon(mime) {
  if (!mime) return File;
  if (mime.includes('pdf'))   return FileText;
  if (mime.includes('image')) return Image;
  if (mime.includes('sheet') || mime.includes('excel')) return FileSpreadsheet;
  return File;
}

function mimeColor(mime) {
  if (!mime) return '#9CA3AF';
  if (mime.includes('pdf'))   return '#E57373';
  if (mime.includes('image')) return '#4FC3F7';
  if (mime.includes('sheet') || mime.includes('excel')) return '#81C784';
  return '#9CA3AF';
}

function formatSize(bytes) {
  if (!bytes) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── Authenticated blob fetch ──────────────────────────────────────────────
// `axios` carries the auth cookie via `withCredentials: true` (set on the
// shared API instance), so this works whether or not the API is on the same
// origin. Returns an object URL the caller is responsible for revoking.
async function fetchDocumentBlob(doc, { inline = false } = {}) {
  // doc.download_url is "/api/admin/documents/:id/download" but the axios
  // instance already has baseURL ending in "/api", so strip the prefix.
  const path = (doc.download_url || '').replace(/^\/api/, '');
  const url  = `${path}${inline ? '?inline=1' : ''}`;
  const { data } = await API.get(url, { responseType: 'blob' });
  return URL.createObjectURL(data);
}

// Office formats can't render natively in a browser — show a Download placeholder.
function previewKind(mime) {
  if (!mime) return null;
  if (mime.startsWith('image/'))      return 'image';
  if (mime === 'application/pdf')     return 'pdf';
  if (mime.includes('word')   || mime.includes('officedocument.wordprocessing')) return 'office';
  if (mime.includes('excel')  || mime.includes('officedocument.spreadsheet') || mime.includes('sheet')) return 'office';
  return null;
}
const isPreviewable = (mime) => previewKind(mime) !== null;

const ROLE_LABEL = {
  super_admin: 'Super Admin',
  admin:       'Admin',
  ca:          'CA',
  customer:    'Customer',
};

function DocRow({ doc, onPreview }) {
  const Icon  = mimeIcon(doc.mime_type);
  const color = mimeColor(doc.mime_type);
  const isAdmin = doc.document_type?.startsWith('admin_') || doc.document_type?.startsWith('cycle_');
  const uploaderRoleLabel = doc.uploaded_by_role ? ROLE_LABEL[doc.uploaded_by_role] || doc.uploaded_by_role : null;
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const objectUrl = await fetchDocumentBlob(doc, { inline: false });
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = doc.original_name || `document-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick so the browser has time to start the download
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Access denied.' :
               err.response?.status === 404 ? 'File not found.' :
               'Download failed.');
    }
    setDownloading(false);
  };

  return (
    <tr className="border-b border-[#E8E6F8] last:border-0 hover:bg-[#F5F4FB] transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
            <Icon className="h-4 w-4" style={{ color }} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-body text-[#1A1A2E] truncate max-w-[220px]">{doc.original_name}</p>
            <p className="text-xs text-[#9CA3AF] font-body">
              {formatSize(doc.file_size)}
              {doc.uploaded_by_name && (
                <span className="ml-1">· by <span className="text-[#6B7280] font-medium">{doc.uploaded_by_name}</span></span>
              )}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm font-body text-[#4A4A60]">
        {docLabel(doc.document_type)}
        {isAdmin && uploaderRoleLabel && (
          <span className="ml-1.5 text-[10px] text-[#6C63FF] bg-[#6C63FF]/10 px-1.5 py-0.5 rounded-full">
            {uploaderRoleLabel}
          </span>
        )}
        {isAdmin && !uploaderRoleLabel && (
          <span className="ml-1.5 text-[10px] text-[#6C63FF] bg-[#6C63FF]/10 px-1.5 py-0.5 rounded-full">Admin</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-[#9CA3AF] font-body whitespace-nowrap">
        {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString('en-IN') : '–'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 whitespace-nowrap">
          {isPreviewable(doc.mime_type) && (
            <button
              onClick={() => onPreview(doc)}
              className="flex items-center gap-1 text-xs font-body text-[#6C63FF] hover:underline"
            >
              <Eye className="h-3.5 w-3.5" />
              Preview
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center gap-1 text-xs font-body text-[#6C63FF] hover:underline disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {downloading ? '…' : 'Download'}
          </button>
        </div>
        {error && <p className="text-[10px] text-red-500 font-body mt-1">{error}</p>}
      </td>
    </tr>
  );
}

// ── Preview modal ─────────────────────────────────────────────────────────
function PreviewModal({ doc, onClose }) {
  const [url, setUrl]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    let active = true;
    let createdUrl = null;
    setLoading(true);
    setError('');
    fetchDocumentBlob(doc, { inline: true })
      .then((u) => {
        if (!active) { URL.revokeObjectURL(u); return; }
        createdUrl = u;
        setUrl(u);
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err.response?.status === 403 ? 'Access denied.' :
          err.response?.status === 404 ? 'File not found.' :
          'Failed to load preview.'
        );
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [doc]);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const kind = previewKind(doc.mime_type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6" onClick={onClose}>
      <div
        className="w-full max-w-5xl h-full max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E6F8]">
          <div className="min-w-0">
            <p className="text-sm font-heading font-bold text-[#1A1A2E] truncate">{doc.original_name}</p>
            <p className="text-xs text-[#9CA3AF] font-body">{docLabel(doc.document_type)} · {formatSize(doc.file_size)}</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#1A1A2E]" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-[#F5F4FB] flex items-center justify-center">
          {loading && (
            <div className="w-8 h-8 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          )}
          {error && !loading && (
            <p className="text-sm text-red-500 font-body">{error}</p>
          )}
          {url && !loading && !error && (
            kind === 'image' ? (
              <img src={url} alt={doc.original_name} className="max-w-full max-h-full object-contain" />
            ) : kind === 'pdf' ? (
              // No `sandbox` here — Chrome refuses to render PDFs in sandboxed
              // iframes. The blob URL is same-origin so this is safe.
              <iframe src={url} title={doc.original_name} className="w-full h-full" />
            ) : (
              <OfficePreviewFallback doc={doc} url={url} />
            )
          )}
        </div>
      </div>
    </div>
  );
}

function OfficePreviewFallback({ doc, url }) {
  return (
    <div className="text-center px-6 py-12">
      <File className="h-14 w-14 text-[#9CA3AF] mx-auto mb-3" />
      <p className="text-sm font-heading font-semibold text-[#1A1A2E]">
        Preview not available in browser
      </p>
      <p className="text-xs text-[#6B7280] font-body mt-1 max-w-md mx-auto">
        Excel and Word files can't be rendered inline. Download the file and open it in
        the matching desktop application.
      </p>
      <a
        href={url}
        download={doc.original_name}
        className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg bg-[#6C63FF] hover:bg-[#5B52CC] text-white text-xs font-body"
      >
        <Download className="h-3.5 w-3.5" /> Download {doc.original_name}
      </a>
    </div>
  );
}

function StepGroup({ stepNum, stepName, docs, onPreview }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-xl border border-[#E8E6F8] overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#F5F4FB] hover:bg-[#EEEDFC] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-heading font-bold text-[#6C63FF] bg-[#6C63FF]/10 px-2 py-0.5 rounded-full">
            Step {stepNum}
          </span>
          <span className="text-sm font-heading font-semibold text-[#1A1A2E]">{stepName}</span>
          <span className="text-xs text-[#9CA3AF] font-body">({docs.length})</span>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-[#9CA3AF]" /> : <ChevronRight className="h-4 w-4 text-[#9CA3AF]" />}
      </button>
      {open && (
        <table className="w-full text-xs font-body">
          <thead>
            <tr className="border-b border-[#E8E6F8]">
              {['File', 'Document Type', 'Uploaded At', ''].map((h) => (
                <th key={h} className="px-4 py-2 text-left font-heading font-semibold text-[#9CA3AF] text-[10px] uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => <DocRow key={d.id} doc={d} onPreview={onPreview} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function DocumentsList({ documents = [], orderId, steps = [], onRefresh }) {
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileRef = useRef(null);
  const [stepNum, setStepNum]     = useState('');
  const [label, setLabel]         = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    if (!stepNum) { setError('Please select a step'); return; }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('deliverable', file);
      if (label) fd.append('label', label);
      await API.post(`/admin/orders/${orderId}/steps/${stepNum}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLabel('');
      setStepNum('');
      if (fileRef.current) fileRef.current.value = '';
      if (onRefresh) await onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  // Keep admin / CA / Super Admin uploads from this tab in their own
  // section so they're easy to scan separately from what the client
  // submitted per-step. The uploader name + role pill on each row makes
  // it clear who uploaded each file.
  const userDocs  = documents.filter((d) => !d.document_type?.startsWith('admin_') && !d.document_type?.startsWith('cycle_'));
  const adminDocs = documents.filter((d) =>  d.document_type?.startsWith('admin_') ||  d.document_type?.startsWith('cycle_'));

  // Group client docs by step_number
  const stepGroups = steps.reduce((acc, s) => {
    const grouped = userDocs.filter((d) => d.step_number === s.step_number);
    if (grouped.length > 0) acc.push({ stepNum: s.step_number, stepName: s.name, docs: grouped });
    return acc;
  }, []);

  // Client docs with no matching step
  const ungrouped = userDocs.filter((d) => !steps.some((s) => s.step_number === d.step_number));

  return (
    <div className="space-y-6">
      {/* Client submitted — grouped by step */}
      <div>
        <h3 className="text-sm font-heading font-semibold text-[#1A1A2E] mb-3">
          Client Documents ({userDocs.length})
        </h3>
        {userDocs.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] font-body">No documents uploaded by client yet</p>
        ) : (
          <div className="space-y-3">
            {stepGroups.map(({ stepNum: sn, stepName, docs }) => (
              <StepGroup key={sn} stepNum={sn} stepName={stepName} docs={docs} onPreview={setPreviewDoc} />
            ))}
            {ungrouped.length > 0 && (
              <StepGroup stepNum="?" stepName="Unassigned" docs={ungrouped} onPreview={setPreviewDoc} />
            )}
          </div>
        )}
      </div>

      {/* Admin / CA / Super Admin uploaded — separate card. Each DocRow shows
          the actual uploader name + role pill so it's clear who put it here. */}
      {adminDocs.length > 0 && (
        <div>
          <h3 className="text-sm font-heading font-semibold text-[#1A1A2E] mb-3">
            Admin Uploaded ({adminDocs.length})
          </h3>
          <div className="bg-white rounded-xl border border-[#E8E6F8] overflow-hidden">
            <table className="w-full text-xs font-body">
              <thead>
                <tr className="border-b border-[#E8E6F8] bg-[#F5F4FB]">
                  {['File', 'Document Type', 'Uploaded At', ''].map((h) => (
                    <th key={h} className="px-4 py-2 text-left font-heading font-semibold text-[#9CA3AF] text-[10px] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adminDocs.map((d) => <DocRow key={d.id} doc={d} onPreview={setPreviewDoc} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Heads-up banner — clarifies that this tab's upload is for
          internal/reference files only. To deliver to the client, the
          Workflow tab's per-step "Upload Draft" action is the right path. */}
      <div className="bg-[#F5F4FB] border border-[#E8E6F8] rounded-xl p-4 flex items-start gap-3">
        <Upload className="h-4 w-4 text-[#6C63FF] mt-0.5 flex-shrink-0" />
        <p className="text-xs font-body text-[#4A4A60] leading-relaxed">
          This tab is a read-only view of every file uploaded against this order.
          To <span className="font-semibold text-[#1A1A2E]">send a document to the client</span>, open the{' '}
          <span className="font-semibold text-[#1A1A2E]">Workflow tab</span> and use the per-step
          "Upload Draft" action — the file will appear in the client's workflow with version history.
        </p>
      </div>

      {/* Upload section — stores the file against the order for internal
          reference. Does not notify the client (use the Workflow tab for
          that — see the banner above). */}
      <div className="bg-white border border-[#E8E6F8] rounded-xl p-4 space-y-3">
        <p className="text-sm font-heading font-semibold text-[#1A1A2E]">Upload Document for Client</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-body text-[#6B7280] mb-1 block">Step</label>
            <select
              value={stepNum}
              onChange={(e) => setStepNum(e.target.value)}
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            >
              <option value="">Select step…</option>
              {steps.map((s) => (
                <option key={s.step_number} value={s.step_number}>
                  {s.step_number}. {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-body text-[#6B7280] mb-1 block">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Filed ITR"
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
            className="flex-1 text-xs font-body text-[#6B7280] file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-[#6C63FF]/10 file:text-[#6C63FF] file:text-xs file:font-body cursor-pointer"
          />
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="flex items-center gap-1.5 text-xs font-body font-medium px-4 py-2 bg-[#6C63FF] hover:bg-[#5B52CC] text-white rounded-lg transition-colors disabled:opacity-60 whitespace-nowrap"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>

        {error && <p className="text-xs text-red-500 font-body">{error}</p>}
      </div>

      {previewDoc && (
        <PreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </div>
  );
}
